// ClientUploadLinkModal — advisor-side mint + manage UI for the
// magic-link client upload feature.
//
// CONSUMER: internal-ui (advisor pages — DocumentsTab modal).
//
// v1.x phase 11.16 (Tier B item 11). Per Grant's spec:
//   * Multi-use within an advisor-selected window.
//   * Email notification to the advisor when an upload lands
//     (handled server-side; this UI doesn't render that).
//
// UI:
//   * Existing tokens for this entity render as a list with
//     status pill (Active / Expired / Revoked), expiry, upload
//     count, and a "Revoke" button on active rows.
//   * Mint form: window picker (presets 7d / 14d / 30d /
//     custom), recipient label, optional prompt note. Submit
//     mints + reveals the URL with a Copy button. Raw token
//     never re-appears after this modal closes.

import { useCallback, useEffect, useState } from "react";

import {
  apiV11,
  ApiError,
  type ClientUploadTokenSummary,
  type MintClientUploadTokenResponse,
} from "../api";
import { Button, ErrorBanner } from "../ui";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

const WINDOW_PRESETS = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

const DEFAULT_WINDOW_DAYS = 14;

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function relativeRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return "—";
  if (ms <= 0) return "expired";
  const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
  if (days <= 1) return "<1 day left";
  if (days < 30) return `${days} days left`;
  const months = Math.round(days / 30);
  return `~${months} month${months === 1 ? "" : "s"} left`;
}

export interface ClientUploadLinkModalProps {
  open: boolean;
  onClose: () => void;
  entityType: "household" | "contact";
  entityId: string;
  // Advisor-supplied default for the "recipient_label" field.
  // Usually "Smith Family" / "Mary Smith". We pre-fill but let
  // the advisor edit before mint.
  defaultRecipientLabel: string | null;
}

export function ClientUploadLinkModal({
  open,
  onClose,
  entityType,
  entityId,
  defaultRecipientLabel,
}: ClientUploadLinkModalProps) {
  const toast = useToast();
  const [tokens, setTokens] = useState<ClientUploadTokenSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [windowDays, setWindowDays] = useState<number>(DEFAULT_WINDOW_DAYS);
  const [recipientLabel, setRecipientLabel] = useState<string>("");
  const [promptNote, setPromptNote] = useState<string>("");
  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  const [mintResult, setMintResult] =
    useState<MintClientUploadTokenResponse | null>(null);

  // Reset the form every time the modal opens. Keeps the raw-token
  // reveal from leaking across modal sessions.
  useEffect(() => {
    if (!open) return;
    setMintError(null);
    setMintResult(null);
    setRecipientLabel(defaultRecipientLabel ?? "");
    setPromptNote("");
    setWindowDays(DEFAULT_WINDOW_DAYS);
    void loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const resp = await apiV11.listClientUploadTokens(entityType, entityId);
      setTokens(resp.tokens);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : "Couldn't load links.");
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  const mintNow = useCallback(async () => {
    setMinting(true);
    setMintError(null);
    try {
      const resp = await apiV11.mintClientUploadToken({
        entity_type: entityType,
        entity_id: entityId,
        recipient_label: recipientLabel.trim() || null,
        prompt_note: promptNote.trim() || null,
        window_days: windowDays,
      });
      setMintResult(resp);
      // Refresh the list so the new token appears.
      void loadTokens();
    } catch (err) {
      setMintError(err instanceof ApiError ? err.message : "Mint failed.");
    } finally {
      setMinting(false);
    }
  }, [
    entityType,
    entityId,
    recipientLabel,
    promptNote,
    windowDays,
    loadTokens,
  ]);

  const revokeToken = useCallback(
    async (tokenId: string) => {
      try {
        await apiV11.revokeClientUploadToken(tokenId);
        await loadTokens();
        toast.success("Link revoked.");
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Revoke failed.");
      }
    },
    [loadTokens, toast],
  );

  // The advisor copies the FULL URL (origin + path) to share — we
  // assemble it from the page's current origin so dev / staging /
  // production all produce the right value.
  const fullUrlFor = (path: string): string =>
    typeof window !== "undefined"
      ? `${window.location.origin}${path}`
      : path;

  const copyToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Link copied.");
      } catch {
        toast.error("Couldn't access clipboard.");
      }
    },
    [toast],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload links"
      className="client-upload-link-modal"
      data-testid="client-upload-link-modal"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Done
        </Button>
      }
    >
      <p className="client-upload-link-intro">
        Generate a link your client can open to upload documents — no
        Stewardship account required. You'll get an email when an
        upload lands.
      </p>

      {/* ── Existing tokens ─────────────────────────────────── */}
      <section className="client-upload-link-section">
        <header className="client-upload-link-section-head">
          <h3>Existing links</h3>
          <Button
            variant="ghost"
            onClick={() => void loadTokens()}
            disabled={loading}
          >
            Refresh
          </Button>
        </header>
        {listError ? <ErrorBanner message={listError} /> : null}
        {loading && tokens.length === 0 ? (
          <p className="client-upload-link-empty">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="client-upload-link-empty">
            No links yet. Generate one below.
          </p>
        ) : (
          <ul className="client-upload-link-list">
            {tokens.map((t) => (
              <li
                key={t.id}
                className={`client-upload-link-row client-upload-link-${t.status}`}
              >
                <div className="client-upload-link-row-main">
                  <span className={`client-upload-link-status client-upload-link-status-${t.status}`}>
                    {t.status}
                  </span>
                  <span className="client-upload-link-row-recipient">
                    {t.recipient_label ?? "(no label)"}
                  </span>
                </div>
                <div className="client-upload-link-row-meta">
                  <span>
                    Expires {formatExpiry(t.expires_at)}
                    {t.status === "active" ? ` · ${relativeRemaining(t.expires_at)}` : ""}
                  </span>
                  <span>
                    {t.upload_count}{" "}
                    {t.upload_count === 1 ? "upload" : "uploads"}
                  </span>
                </div>
                {t.status === "active" ? (
                  <Button
                    variant="ghost"
                    onClick={() => void revokeToken(t.id)}
                  >
                    Revoke
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Mint form ───────────────────────────────────────── */}
      <section className="client-upload-link-section">
        <header className="client-upload-link-section-head">
          <h3>Generate new link</h3>
        </header>

        {mintResult ? (
          <div className="client-upload-link-mint-result">
            <p className="client-upload-link-mint-success">
              Link generated. Copy it below — you won't see this URL
              again after closing.
            </p>
            <div className="client-upload-link-url-row">
              <code className="client-upload-link-url">
                {fullUrlFor(mintResult.path)}
              </code>
              <Button
                variant="primary"
                onClick={() =>
                  void copyToClipboard(fullUrlFor(mintResult.path))
                }
              >
                Copy link
              </Button>
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setMintResult(null);
                setRecipientLabel(defaultRecipientLabel ?? "");
                setPromptNote("");
              }}
            >
              Generate another
            </Button>
          </div>
        ) : (
          <div className="client-upload-link-mint-form">
            {mintError ? <ErrorBanner message={mintError} /> : null}

            <label className="client-upload-link-field">
              <span>Recipient label (shown on upload page)</span>
              <input
                type="text"
                value={recipientLabel}
                onChange={(e) => setRecipientLabel(e.target.value)}
                placeholder="e.g. Smith Family"
                maxLength={200}
              />
            </label>

            <label className="client-upload-link-field">
              <span>Prompt for the client (optional)</span>
              <textarea
                value={promptNote}
                onChange={(e) => setPromptNote(e.target.value)}
                placeholder="e.g. Please upload your 2024 tax docs."
                rows={2}
                maxLength={1000}
              />
            </label>

            <div className="client-upload-link-field">
              <span>Valid for</span>
              <div
                className="client-upload-link-window"
                role="radiogroup"
              >
                {WINDOW_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    role="radio"
                    aria-checked={windowDays === p.days}
                    className={`client-upload-link-window-btn${windowDays === p.days ? " is-active" : ""}`}
                    onClick={() => setWindowDays(p.days)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <Button
              variant="primary"
              onClick={() => void mintNow()}
              disabled={minting}
            >
              {minting ? "Generating…" : "Generate link"}
            </Button>
          </div>
        )}
      </section>
    </Modal>
  );
}
